import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Facebook, MessageCircle, Phone, Link2, Share2, CheckCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { jobShareLink, jobShareText, facebookShareUrl, messengerShareUrl, viberShareUrl } from '../lib/shareJob';

// "Get more applicants: share this job" — shown to the employer right after posting (justPosted)
// and from the Share button on each open job in their dashboard. One tap to their Facebook page,
// Messenger, Viber, or copy the link; every option uses the preview link (openings, pay, image).
const ShareJobDialog = ({ job, open, onClose, justPosted = false }) => {
  const { t } = useTranslation();
  if (!job) return null;
  const link = jobShareLink(job);
  const text = jobShareText(job);

  const openApp = (url, note) => {
    const w = window.open(url, '_blank', 'noopener');
    if (note) toast.success(note, { duration: 2000 });
    return w;
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t('shareJob.copied', 'Link copied — paste it in your page, group chats or status'));
    } catch (e) {
      toast.info(`${t('shareJob.copyManually', 'Copy this link')}: ${link}`, { duration: 8000 });
    }
  };

  const nativeShare = async () => {
    try {
      await navigator.share({ title: job.title, text, url: link });
    } catch (e) {
      /* cancelled */
    }
  };

  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="share-job-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6 text-left leading-snug">
            {justPosted && <CheckCircle size={20} className="text-green-600 flex-shrink-0" />}
            {justPosted
              ? t('shareJob.liveTitle', 'Your job is live! Get more applicants')
              : t('shareJob.title', 'Share this job')}
          </DialogTitle>
          <DialogDescription className="text-left">
            {t('shareJob.why', 'Share it on your business page, with your staff and customers, and in your group chats. Every share reaches job seekers near you — for free.')}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border bg-muted/40 p-3 text-sm break-words" data-testid="share-job-preview">
          {text}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button className="rounded-xl bg-[#1877F2] hover:bg-[#166FE5] text-white" data-testid="share-job-facebook"
            onClick={() => openApp(facebookShareUrl(link), t('shareJob.openingFacebook', 'Opening Facebook...'))}>
            <Facebook size={16} className="mr-2" /> Facebook
          </Button>
          <Button className="rounded-xl bg-[#0084FF] hover:bg-[#0077E6] text-white" data-testid="share-job-messenger"
            onClick={() => openApp(messengerShareUrl(link), t('shareJob.openingMessenger', 'Opening Messenger...'))}>
            <MessageCircle size={16} className="mr-2" /> Messenger
          </Button>
          <Button className="rounded-xl bg-[#7360F2] hover:bg-[#665AD8] text-white" data-testid="share-job-viber"
            onClick={() => openApp(viberShareUrl(text, link), t('shareJob.openingViber', 'Opening Viber...'))}>
            <Phone size={16} className="mr-2" /> Viber
          </Button>
          <Button variant="outline" className="rounded-xl" data-testid="share-job-copy" onClick={copyLink}>
            <Link2 size={16} className="mr-2" /> {t('shareJob.copyLink', 'Copy link')}
          </Button>
          {canNativeShare && (
            <Button variant="outline" className="rounded-xl col-span-2" data-testid="share-job-more" onClick={nativeShare}>
              <Share2 size={16} className="mr-2" /> {t('shareJob.more', 'More apps…')}
            </Button>
          )}
        </div>

        <DialogFooter>
          <Button variant={justPosted ? 'default' : 'outline'} className="w-full rounded-xl" onClick={onClose} data-testid="share-job-done">
            {justPosted ? t('shareJob.toDashboard', 'Go to my dashboard') : t('common.close', 'Close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShareJobDialog;
